<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { HaftarahType, ParashahType } from 'types/types'
import { useForm } from 'vee-validate'
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import * as yup from 'yup'

import DialogTitleBar from '@/components/DialogTitleBar.vue'
import OwnerFieldRow from '@/components/OwnerFieldRow.vue'
import ParashahSelect from '@/components/ParashahSelect.vue'
import VerseSelect from '@/components/VerseSelect.vue'
import { formattedParashah } from '@/composables/format'
import { useDefaultOwner } from '@/composables/useDefaultOwner'
import { useUtil } from '@/composables/util'
import { YEAR_FIELD_MIN_WIDTH } from '@/constants/practiceConstants'
import store from '@/store'
import { compareYear } from '@/util/Aliyah'
import { extractRowItem, sortUserThenRoot, yearFieldSchema } from '@/util/customReading'
import { CreateHaftarahParameters, HAFTARAH_VARIANT_ASHKENAZI, HAFTARAH_VARIANT_SEPHARDIC } from '@/util/Haftarah'

const logger = useLogger()
const { isStaff, selectableUsers } = useUtil(logger)

interface CustomHaftarahData {
  haftarahId: string | null
  includeRootHaftarah: boolean
  tableHeaders: Array<object>
  formVisible: boolean
  parashah: ParashahType | null
  sections: Array<string>
}

const state = reactive<CustomHaftarahData>({
  formVisible: false,
  haftarahId: null,
  includeRootHaftarah: false,
  parashah: null,
  sections: new Array<string>(''),
  tableHeaders: [],
})

const { defineField, errors, handleSubmit, resetForm } = useForm({
  validationSchema: toTypedSchema(
    yup.object({
      comment: yup.string(),
      owner: yup.string().required(),
      variant: yup.string().trim(),
      year: yearFieldSchema,
    }),
  ),
})

const [comment, commentAttrs] = defineField('comment')
const [owner, ownerAttrs] = defineField('owner')
const [variant, variantAttrs] = defineField('variant')
const [year, yearAttrs] = defineField('year')

const applyDefaultOwner = useDefaultOwner(owner)
const haftarahVerseSelect = ref<Array<InstanceType<typeof VerseSelect>> | null>(null)

onMounted(() => {
  resetNewHaftarah()
  store.dispatch.user.fetchUser()
  store.dispatch.teacher.fetchHaftarot({ mine: !isStaff(), parashah: null })
  state.tableHeaders = [
    { key: 'year', title: 'Year' },
    { key: 'parashah', title: 'Parashah' },
  ]
  if (isStaff()) {
    state.tableHeaders.push({ key: 'reading.user.username', title: 'User' })
  }
  state.tableHeaders.push(
    { key: 'section', sortable: false, title: 'Section' },
    {
      key: 'variant',
      sortable: false,
      title: 'Variant (Ashkenazi/Sephardic)',
    },
    { key: 'comment', sortable: false, title: 'Comment' },
    { key: 'action', sortable: false, title: ' ' },
  )
})

const canSubmit = computed(() => !!owner.value && !!state.parashah && state.sections.some((s) => s && s.trim().length > 0))

const compareHaftarot = (a: HaftarahType, b: HaftarahType) => a.parashah.order - b.parashah.order || compareYear(a, b)

const sortedHaftarot = computed<Array<HaftarahType>>(() =>
  sortUserThenRoot(store.getters.teacher.haftarot, {
    compare: compareHaftarot,
    currentUsername: store.getters.user.user!.username,
    includeRoot: state.includeRootHaftarah,
    usernameOf: (haftarah) => haftarah.reading.user.username,
  }),
)

function addHaftarah() {
  resetNewHaftarah()
  state.sections = new Array<string>('') // default to one empty section
  state.parashah = null
  state.formVisible = true
  nextTick(() => {
    clearSection(0)
  })
}

const isHaftarah = (candidate: unknown): candidate is HaftarahType => !!(candidate as Partial<HaftarahType> | null | undefined)?.reading

const editHaftarah = (...args: unknown[]) => {
  const haftarah = extractRowItem(isHaftarah, ...args)

  if (!haftarah) {
    logger.debug('unexpected null haftarah in editHaftarah')
    return
  }

  state.haftarahId = haftarah.reading.id
  state.formVisible = true
  state.parashah = haftarah.parashah
  owner.value = haftarah.reading.user.username
  year.value = haftarah.year
  variant.value = haftarah.variant ?? ''

  const parsedSections = haftarah.section.split(/\s*;\s*/)
  state.sections = parsedSections
  repopulateSections(parsedSections)
}

function repopulateSections(sections: Array<string>): void {
  sections.forEach((section: string, index: number) => {
    populateSection(index, section)
  })
}

const MAX_REF_WAIT_RETRIES = 40
const REF_POLL_INTERVAL_MS = 50

function waitForVerseSelectRef(index: number, action: (vs: InstanceType<typeof VerseSelect>) => void, retries = 0) {
  if (haftarahVerseSelect.value && haftarahVerseSelect.value[index]) {
    action(haftarahVerseSelect.value[index])
  } else if (retries < MAX_REF_WAIT_RETRIES) {
    setTimeout(() => {
      waitForVerseSelectRef(index, action, retries + 1)
    }, REF_POLL_INTERVAL_MS)
  }
}

function clearSection(index: number) {
  waitForVerseSelectRef(index, (vs) => vs.clear())
}

function populateSection(index: number, section: string) {
  waitForVerseSelectRef(index, (vs) => vs.update(section))
}

function resetNewHaftarah() {
  state.haftarahId = null
  state.sections = []
  resetForm()

  applyDefaultOwner()
}

const handleCreateHaftarah = handleSubmit(async () => {
  const saved = await store.dispatch.teacher.createHaftarah({
    comment: comment.value ?? '',
    id: parseInt(state.haftarahId ?? ''),
    parashah: state.parashah,
    section: state.sections.join(';'),
    username: owner.value ?? '',
    variant: variant.value ?? '',
    year: year.value ?? null,
  } as CreateHaftarahParameters)
  if (saved) {
    state.formVisible = false
    resetNewHaftarah()
  }
})

async function confirmDeleteHaftarah(haftarah: HaftarahType) {
  store.dispatch.dialog.show({
    isRejectable: true,
    message: 'Are you sure you want to delete this haftarah?',
    onAccept: () => store.dispatch.teacher.deleteHaftarah({ haftarah }),
    title: 'Confirm',
  })
}
</script>

<template>
  <v-container fluid>
    <!-- Add Haftarah -->
    <v-btn color="primary" @click="addHaftarah"> Add Haftarah </v-btn>

    <template v-if="store.getters.teacher.haftarot && store.getters.teacher.haftarot.length">
      <v-switch v-if="isStaff()" v-model="state.includeRootHaftarah" color="primary" label="Include system haftarot" />
      <v-data-table
        :headers="state.tableHeaders"
        :items="sortedHaftarot"
        no-data-text="[no haftarot in this selection]"
        :sort-by="[{ key: 'reading.description', order: 'asc' }]"
        @click:row="editHaftarah"
      >
        <template #item.parashah="{ item }">
          <span>{{ formattedParashah(item.parashah) }}</span>
        </template>
        <template #item.action="{ item }">
          <v-btn icon variant="plain" @click.capture.stop="() => editHaftarah(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            color="error"
            icon
            variant="plain"
            @click.capture.stop="
              () => {
                confirmDeleteHaftarah(item)
              }
            "
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </template>

    <!-- edit Haftarah -->
    <v-dialog id="newHaftarah" v-model="state.formVisible" class="mb-4 pb-4" no-click-animation @keydown.esc="state.formVisible = false">
      <v-form @submit="handleCreateHaftarah">
        <v-card>
          <dialog-title-bar :title="`${state.haftarahId ? 'Change' : 'Add'} Haftarah`" @close="state.formVisible = false" />
          <v-card-text>
            <owner-field-row
              v-model="owner"
              :error-messages="errors.owner"
              :items="selectableUsers"
              :owner-attrs="ownerAttrs"
              row-class="mt-3 pt-3"
            />
            <v-row>
              <v-col md="1">
                <v-text-field
                  v-model="year"
                  v-bind="yearAttrs"
                  :error-messages="errors.year"
                  label="Year"
                  placeholder="Year"
                  :style="{ minWidth: YEAR_FIELD_MIN_WIDTH }"
                  type="number"
                  variant="underlined"
                />
              </v-col>
              <v-col md="3">
                <parashah-select
                  v-model:parashah="state.parashah"
                  :clearable="false"
                  :external-set="true"
                  :reset-selection-on-multiple="false"
                />
              </v-col>
              <v-col md="2">
                <!-- variant not required -->
                <v-autocomplete
                  v-model="variant"
                  v-bind="variantAttrs"
                  :clearable="false"
                  :error-messages="errors.variant"
                  :items="[
                    { title: 'Ashkenazi', value: HAFTARAH_VARIANT_ASHKENAZI },
                    { title: 'Sephardic', value: HAFTARAH_VARIANT_SEPHARDIC },
                    { title: 'Both', value: '' },
                  ]"
                  label="Ashkenazi / Sephardic"
                  variant="underlined"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col md="3">
                <v-text-field
                  v-model="comment"
                  v-bind="commentAttrs"
                  :error-messages="errors.comment"
                  label="Comment"
                  placeholder=""
                  type="text"
                  variant="underlined"
                />
              </v-col>
            </v-row>
            <v-row v-for="(__, index) in state.sections" :key="'haftarahSection-' + index">
              <v-col>
                <v-card outlined width="100%">
                  <v-card-subtitle>Haftarah Section {{ index + 1 }}</v-card-subtitle>
                  <v-card-text>
                    <verse-select
                      ref="haftarahVerseSelect"
                      v-model:section="state.sections[index]"
                      :range="true"
                      :selected-section="state.sections[index]"
                    />
                    <v-btn
                      @click="
                        () => {
                          state.sections.splice(index, 1)
                          repopulateSections(state.sections)
                        }
                      "
                      >Delete section</v-btn
                    >
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col md="2">
                <v-btn
                  block
                  @click="
                    () => {
                      state.sections.push('')
                    }
                  "
                  >Add section</v-btn
                >
              </v-col>
            </v-row>

            <!-- buttons -->
            <v-row>
              <v-col md="1">
                <v-btn block color="primary" :disabled="!canSubmit" type="submit">{{ state.haftarahId ? 'Update' : 'Add' }} </v-btn>
              </v-col>
              <v-col md="1">
                <v-btn block variant="plain" @click="state.formVisible = false">Cancel</v-btn>
              </v-col>
              <v-col md="1">
                <v-btn block color="red-lighten-3" @click="resetNewHaftarah">Reset</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>
    </v-dialog>
  </v-container>
</template>

<style scoped></style>
